import type {
  CartAddRequest,
  CartItem,
  CartRemoveRequest,
  CartTotalRequest,
  CartTransferRequest,
} from "@features/shopping/types";

import { useAuthStore } from "@features/authentication/stores";
import { cartService } from "@features/shopping/api";
import { handleApiError } from "@shared/utils/error";
import { toast } from "@ui/toast";
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";

type CartState = {
  cartId: null | string;
  carts: CartItem[];
  error: null | string;
  loading: boolean;
  open: boolean;
  pendingOperations: Set<number>;
  shippingTotal: number;
  subTotal: number;
};

export const useCartStore = defineStore("cart", {
  actions: {
    async addItem({
      limitAmount,
      productSkuId,
      quantity,
      update = false,
    }: {
      limitAmount: number;
      productSkuId: number;
      quantity: number;
      update?: boolean;
    }) {
      // Prevent duplicate operations for the same SKU
      if (!update && this.pendingOperations.has(productSkuId)) {
        this.error = "Please wait, item is being added to cart";
        return;
      }

      this.loading = true;
      this.error = null;

      // Add to pending operations to prevent race conditions
      if (!update) {
        this.pendingOperations.add(productSkuId);
      }

      const existingItem = this.carts.find((item) => item.product_sku_id === productSkuId);

      if (!update && existingItem) {
        const newTotalQuantity = existingItem.quantity + quantity;
        if (newTotalQuantity > limitAmount) {
          this.error = `Maximum quantity for ${existingItem.product_name} is ${limitAmount}`;
          this.pendingOperations.delete(productSkuId);
          this.loading = false;
          return;
        }
      }

      try {
        let totalQuantity = quantity;
        if (!update && existingItem) {
          totalQuantity = existingItem.quantity + quantity;
        }

        const request: CartAddRequest = {
          member_id: this.getMemberId,
          member_type: this.getMemberType,
          product_sku_id: productSkuId.toString(),
          quantity: totalQuantity.toString(),
        };

        const response = await cartService.addCart(request);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        // Save cart ID if returned and valid
        if (response.cart_id && typeof response.cart_id === "string" && response.cart_id.trim()) {
          this.cartId = response.cart_id;
          localStorage.setItem("cart_id", response.cart_id);
        }

        // Refresh cart data
        await this.fetchCartTotal();

        return response;
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to add item to cart");
        this.error = errorMessage;
        toast({
          description: errorMessage,
          title: "Error",
          variant: "destructive",
        });
        throw err;
      } finally {
        // Remove from pending operations
        if (!update) {
          this.pendingOperations.delete(productSkuId);
        }
        this.loading = false;
      }
    },

    clearCart() {
      this.carts = [];
      this.shippingTotal = 0;
      this.subTotal = 0;
      this.pendingOperations.clear();
      localStorage.removeItem("cart_id");
      this.cartId = null;
    },

    clearError() {
      this.error = null;
    },

    async fetchCartTotal(inputCountryCode?: string) {
      this.loading = true;
      this.error = null;

      try {
        const authStore = useAuthStore();
        const request: CartTotalRequest = {
          member_id: this.getMemberId,
        };

        const countryCode = inputCountryCode ?? authStore.currentUser?.country_code;

        // Include country code if provided
        if (countryCode) {
          request.country_code = countryCode;
        }

        const response = await cartService.totalCart(request);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        this.carts = response.carts || [];
        this.subTotal = response.sub_total;

        this.shippingTotal = response.shipping_total;

        if (response.carts && response.carts.length > 0 && response.carts[0].cart_id) {
          const cartId = response.carts[0].cart_id;
          if (typeof cartId === "string" && cartId.trim()) {
            this.cartId = cartId;
            localStorage.setItem("cart_id", cartId);
          }
        }

        return response;
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to get cart total");
        this.error = errorMessage;
        toast({
          description: errorMessage,
          title: "Error",
          variant: "destructive",
        });
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async initializeCart() {
      this.loading = true;
      this.error = null;

      try {
        // Get cart ID from local storage
        const cartId = localStorage.getItem("cart_id");

        if (cartId) {
          this.cartId = cartId;
        }

        // Load cart items using totalCart API
        await this.fetchCartTotal();

        return true;
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to initialize cart");
        this.error = errorMessage;
        // Don't toast initialization errors as they might be expected on first load
        return false;
      } finally {
        this.loading = false;
      }
    },

    async removeItem(productSkuId: string) {
      this.loading = true;
      this.error = null;

      try {
        if (!this.cartId) {
          this.error = "Cart ID is missing";
          return null;
        }

        const request: CartRemoveRequest = {
          cart_id: this.cartId,
          member_id: this.getMemberId,
          product_sku_id: productSkuId,
        };

        const response = await cartService.removeCart(request);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        // Refresh cart data
        await this.fetchCartTotal();

        return response;
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to remove item from cart");
        this.error = errorMessage;
        toast({
          description: errorMessage,
          title: "Error",
          variant: "destructive",
        });
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async transferCart(memberId: string) {
      this.loading = true;
      this.error = null;

      try {
        if (!this.cartId) {
          // No cart to transfer
          return true;
        }

        let guestId = localStorage.getItem("guest_id") ?? sessionStorage.getItem("guest_id");
        const guestData =
          localStorage.getItem("guest_data") ?? sessionStorage.getItem("guest_data");

        if (guestData) {
          try {
            const { id } = JSON.parse(guestData);
            guestId = id;
          } catch (error) {
            console.warn("[Cart] Invalid guest data during transfer", error);
          }
        }

        if (!guestId) {
          // No guest ID to transfer from
          return true;
        }

        const request: CartTransferRequest = {
          cart_id: this.cartId,
          guest_id: guestId,
          member_id: memberId,
        };

        const response = await cartService.transferCart(request);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        localStorage.removeItem("guest_id");
        sessionStorage.removeItem("guest_id");
        localStorage.removeItem("guest_data");
        sessionStorage.removeItem("guest_data");

        // Refresh cart data
        await this.fetchCartTotal();

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Failed to transfer cart");
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },

  getters: {
    cartsCount: (state) => state.carts.reduce((acc, cart) => acc + cart.quantity, 0),
    getCartId: (state) => state.cartId,
    getCarts: (state) => state.carts,
    getMemberId: () => {
      const authStore = useAuthStore();
      if (authStore.isAuthenticated && authStore.currentUser?.member_id) {
        return authStore.currentUser.member_id;
      }

      const existingGuestId =
        localStorage.getItem("guest_id") ?? sessionStorage.getItem("guest_id");
      if (existingGuestId) {
        return existingGuestId;
      }

      const guestData = localStorage.getItem("guest_data") ?? sessionStorage.getItem("guest_data");
      if (guestData) {
        try {
          const { id, timestamp } = JSON.parse(guestData);
          const GUEST_EXPIRY_HOURS = 24; // 24 hours expiry
          const isExpired = Date.now() - timestamp > GUEST_EXPIRY_HOURS * 60 * 60 * 1000;

          if (!isExpired && id) {
            return id;
          } else {
            // Clean up expired guest data
            localStorage.removeItem("guest_data");
            sessionStorage.removeItem("guest_data");
            localStorage.removeItem("guest_id");
            sessionStorage.removeItem("guest_id");
            localStorage.removeItem("cart_id");
          }
        } catch (error) {
          console.warn("[Cart] Invalid guest data format, generating new guest ID", error);
          localStorage.removeItem("guest_data");
          sessionStorage.removeItem("guest_data");
          localStorage.removeItem("guest_id");
          sessionStorage.removeItem("guest_id");
        }
      }

      const newGuestId = `guest-${uuidv4()}`;
      const guestDataWithTimestamp = {
        id: newGuestId,
        timestamp: Date.now(),
      };
      localStorage.setItem("guest_data", JSON.stringify(guestDataWithTimestamp));
      localStorage.setItem("guest_id", newGuestId);
      return newGuestId;
    },
    getMemberType: () => {
      const authStore = useAuthStore();
      return authStore.isAuthenticated ? "registered" : "guest";
    },
    getShippingTotal: (state) => state.shippingTotal,
    getSubTotal: (state) => state.subTotal,
    hasCarts: (state) => state.carts.length > 0,
    isCartOpen: (state) => state.open,
  },

  state: (): CartState => ({
    cartId: null,
    carts: [],
    error: null,
    loading: false,
    open: false,
    pendingOperations: new Set(),
    shippingTotal: 0,
    subTotal: 0,
  }),
});
